import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TeamManagementSection = () => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'member'
  });
  const [inviteError, setInviteError] = useState('');

  const [teamMembers, setTeamMembers] = useState([]);

  const roleOptions = [
  { value: 'owner', label: 'Owner', description: 'Full access to all features and settings' },
  { value: 'admin', label: 'Admin', description: 'Manage team, content, and campaigns' },
  { value: 'member', label: 'Member', description: 'Create and schedule content' },
  { value: 'viewer', label: 'Viewer', description: 'View-only access to analytics' }];


  const permissions = {
    owner: ['All Permissions', 'Billing Management', 'Team Management', 'Account Settings'],
    admin: ['Team Management', 'Content Management', 'Campaign Management', 'Analytics Access'],
    member: ['Content Creation', 'Schedule Posts', 'View Analytics', 'Comment Management'],
    viewer: ['View Analytics', 'View Content', 'View Campaigns']
  };

  const handleInviteSubmit = () => {
    if (!inviteForm?.email) {
      setInviteError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(inviteForm?.email)) {
      setInviteError('Invalid email format');
      return;
    }

    const newMember = {
      id: teamMembers?.length + 1,
      name: inviteForm?.email?.split('@')?.[0],
      email: inviteForm?.email,
      avatar: "",
      avatarAlt: "Professional headshot of new team member in business attire with neutral background",
      role: inviteForm?.role,
      status: 'pending',
      joinedDate: new Date()?.toISOString()?.split('T')?.[0]
    };

    setTeamMembers((prev) => [...prev, newMember]);
    setInviteForm({ email: '', role: 'member' });
    setShowInviteForm(false);
    setInviteError('');
  };

  const handleRoleChange = (memberId, newRole) => {
    setTeamMembers((prev) => prev?.map((member) =>
    member?.id === memberId ? { ...member, role: newRole } : member
    ));
  };

  const handleRemoveMember = (memberId) => {
    setTeamMembers((prev) => prev?.filter((member) => member?.id !== memberId));
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-primary/10 text-primary',
      admin: 'bg-accent/10 text-accent',
      member: 'bg-secondary/10 text-secondary',
      viewer: 'bg-muted text-muted-foreground'
    };
    return colors?.[role] || colors?.viewer;
  };

  const getStatusBadgeColor = (status) => {
    return status === 'active' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning';
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Team Members</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your team and their access permissions
            </p>
          </div>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowInviteForm(!showInviteForm)}>

            Invite Member
          </Button>
        </div>

        {showInviteForm &&
        <div className="mb-6 p-4 border border-border rounded-lg bg-muted/50">
            <h3 className="text-sm font-semibold text-foreground mb-4">Invite Team Member</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Input
              label="Email Address"
              type="email"
              value={inviteForm?.email}
              onChange={(e) => {
                setInviteForm((prev) => ({ ...prev, email: e?.target?.value }));
                setInviteError('');
              }}
              error={inviteError}
              placeholder="Enter team member email"
              required />

              <Select
              label="Role"
              options={roleOptions}
              value={inviteForm?.role}
              onChange={(value) => setInviteForm((prev) => ({ ...prev, role: value }))}
              required />

            </div>
            <div className="flex gap-3">
              <Button variant="default" size="sm" onClick={handleInviteSubmit}>
                Send Invitation
              </Button>
              <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowInviteForm(false);
                setInviteError('');
              }}>

                Cancel
              </Button>
            </div>
          </div>
        }

        <div className="space-y-3">
          {teamMembers?.map((member) =>
          <div
            key={member?.id}
            className="flex items-center justify-between gap-4 p-4 border border-border rounded-lg hover:border-primary/50 transition-colors">

              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <Image
                  src={member?.avatar}
                  alt={member?.avatarAlt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">
                      {member?.name}
                    </h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(member?.role)}`}>
                      {member?.role}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(member?.status)}`}>
                      {member?.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{member?.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Joined {new Date(member.joinedDate)?.toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {member?.role !== 'owner' &&
              <>
                    <Select
                  options={roleOptions?.filter((r) => r?.value !== 'owner')}
                  value={member?.role}
                  onChange={(value) => handleRoleChange(member?.id, value)}
                  className="w-32" />

                    <Button
                  variant="outline"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleRemoveMember(member?.id)} />

                  </>
              }
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">Role Permissions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of permissions for each role
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roleOptions?.map((role) =>
          <div key={role?.value} className="border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getRoleBadgeColor(role?.value)}`}>
                  <Icon name="Shield" size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{role?.label}</h3>
                  <p className="text-xs text-muted-foreground">{role?.description}</p>
                </div>
              </div>
              <ul className="space-y-2">
                {permissions?.[role?.value]?.map((permission, idx) =>
              <li key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Check" size={14} className="text-success" />
                    <span>{permission}</span>
                  </li>
              )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>);

};

export default TeamManagementSection;